import {
  FormEvent,
  KeyboardEvent,
  ReactNode,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import cx from "classnames";

import { ArrowUp, Stop } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Textarea from "ds/components/Textarea";
import useAssistantPanelStore from "components/AssistantPanel/Layout/useStore";
import useChatStore from "components/AssistantPanel/Chat/store/useStore";
import Banner from "ds/components/Banner";

import styles from "./styles.module.css";

type AiChatComposerProps = {
  error: Error | undefined;
  onSubmit: (message: string) => void;
  onStop: () => void;
  isStreaming: boolean;
  focusKey?: number;
  composerToolbarSlot?: ReactNode;
  composerFooterSlot?: ReactNode;
  ref?: Ref<AiChatComposerRef>;
};

export type AiChatComposerRef = {
  focus: () => void;
  setValue: (value: string) => void;
  appendValue: (value: string) => void;
};

const AiChatComposer = ({
  error,
  onSubmit,
  onStop,
  isStreaming,
  focusKey,
  composerToolbarSlot,
  composerFooterSlot,
  ref,
}: AiChatComposerProps) => {
  const variant = useAssistantPanelStore((state) => state.variant);
  const isFramed = !!composerFooterSlot;
  const value = useChatStore((state) => state.composerValue);
  const setValue = useChatStore((state) => state.setComposerValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;

    textarea?.focus();
    textarea?.setSelectionRange(textarea.value.length, textarea.value.length);
  }, [variant, focusKey]);

  const submitMessage = () => {
    if (value.trim() && !isStreaming) {
      onSubmit(value.trim());
      setValue("");
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      focus: () => textareaRef.current?.focus(),
      setValue: (value: string) => setValue(value),
      appendValue: (text: string) => {
        const prev = useChatStore.getState().composerValue;

        if (prev.includes(text)) {
          return;
        }

        setValue(prev.trim() ? `${prev.trimEnd()}\n\n${text}` : text);
      },
    }),
    [textareaRef, setValue]
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitMessage();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    }
  };

  const errorMessage = useMemo(() => {
    let result: string | undefined = undefined;

    if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);

        result = parsedError?.message || "An unexpected error occurred";
      } catch {
        result = error.message;
      }
    }

    if (result?.includes("React error")) {
      result = "Something went wrong. Please try again.";
    }

    return result;
  }, [error]);

  return (
    <form onSubmit={handleSubmit}>
      <Box direction="column" gap="medium" padding="x-large">
        {errorMessage && <Banner variant="danger">{errorMessage}</Banner>}
        <Box
          direction="column"
          padding={isFramed ? "small small medium" : undefined}
          gap="medium"
          className={cx(isFramed && styles.composerContainer)}
          relative
        >
          <Box
            direction="column"
            gap="large"
            padding="large"
            relative
            className={cx(styles.messageBox, isFramed && styles.messageBoxFramed)}
            onClick={() => textareaRef.current?.focus()}
          >
            <Textarea
              id="ai-chat-composer-textarea"
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything..."
              className={styles.textarea}
              minRows={1}
            />
            <Box justify="between" align="center" gap="medium" relative>
              {composerToolbarSlot}
              {isStreaming && (
                <ButtonIcon icon={Stop} variant="primary" onPress={onStop}>
                  Stop generating
                </ButtonIcon>
              )}
              {!isStreaming && (
                <ButtonIcon icon={ArrowUp} type="submit" variant="primary" disabled={!value.trim()}>
                  Send message
                </ButtonIcon>
              )}
            </Box>
          </Box>

          {composerFooterSlot}
        </Box>
      </Box>
    </form>
  );
};

export default AiChatComposer;
